<template>
  <!-- eslint-disable vue/no-mutating-props -- account is a nested reactive object from parent form state, bound via v-model -->
  <section class="tenant-details">
    <header>
      <h2 v-if="index === 0">{{ $t('my details') }}</h2>
      <h2 v-else>{{ $t('details tenant :number', { number: String(index + 1) }) }}</h2>
    </header>

    <TenantRelationFields v-if="index > 0" :account="account" :index="index" :errors="errors" />

    <TenantContactFields
      :account="account"
      :index="index"
      :errors="errors"
      @validate-email="emit('validate-email', $event)"
      @apply-did-you-mean="emit('apply-did-you-mean', $event)"
    />

    <fieldset class="address">
      <AddressFields
        :address="account.address"
        :errors="errors"
        :error-prefix="`accounts.${index}.address`"
        :countries="countries"
      />
    </fieldset>

    <fieldset>
      <PersonalDetailsFields :account="account" :index="index" :errors="errors" />
    </fieldset>

    <div class="backup">
      <header>
        <h2 v-if="isStudent">{{ $t('emergency contact / parents') }}</h2>
        <h2 v-else>{{ $t('emergency contact') }}</h2>
        <small>{{ $t('These details are used only in case of emergency.') }}</small>
      </header>

      <BackupContactFields :account="account" :index="index" :errors="errors" :show-name="true" />
    </div>
  </section>

  <section class="financial-details">
    <header>
      <h2 v-if="index === 0">{{ $t('my income') }}</h2>
      <h2 v-else>{{ $t('income tenant :number', { number: String(index + 1) }) }}</h2>
    </header>

    <article v-if="index === 0">
      <p>
        {{
          $t(
            'To apply for this property, you’ll need to provide the income details of everyone included in your application and upload an income statement from the Tax Office under ‘upload documents’.',
          )
        }}
      </p>
    </article>

    <FinancialIncomeFields :account="account" :index="index" :errors="errors" :is-student="isStudent" />
  </section>

  <section class="study-details" v-if="isStudent">
    <header>
      <h2 v-if="index === 0">{{ $t('my study') }}</h2>
      <h2 v-else>{{ $t('study tenant :number', { number: String(index + 1) }) }}</h2>
    </header>

    <fieldset>
      <div class="group">
        <div class="field">
          <label :for="fieldId('account', index, 'details-study-institution')">{{ $t('study institution') }}</label>
          <div>
            <select :id="fieldId('account', index, 'details-study-institution')" v-model="account.details.study.institution">
              <option value="" disabled>-- {{ $t('select an option') }} --</option>
              <option v-for="institution in studyInstitutions" :key="institution.value" :value="institution.value">
                {{ institution.label }}
              </option>
            </select>
            <v-form-error :error="errors[`accounts.${index}.details.study.institution`]" />
          </div>
        </div>

        <div class="field">
          <label>{{ $t('type of study') }}</label>
          <div>
            <div>
              <label class="radio">
                <input type="radio" value="fulltime" v-model="account.details.study.type" />
                <span class="label">{{ $t('study-type:full-time') }}</span>
              </label>
              <label class="radio">
                <input type="radio" value="parttime" v-model="account.details.study.type" />
                <span class="label">{{ $t('study-type:part-time') }}</span>
              </label>
            </div>
            <v-form-error :error="errors[`accounts.${index}.details.study.type`]" />
          </div>
        </div>
      </div>

      <div class="group">
        <div class="field">
          <label :for="fieldId('account', index, 'details-study-faculty')">{{ $t('study faculty') }}</label>
          <div>
            <input :id="fieldId('account', index, 'details-study-faculty')" type="text" v-model="account.details.study.faculty" />
            <v-form-error :error="errors[`accounts.${index}.details.study.faculty`]" />
          </div>
        </div>
      </div>
    </fieldset>
  </section>

  <section class="employment-details" v-if="!isStudent">
    <header>
      <h2 v-if="index === 0">{{ $t('my employment') }}</h2>
      <h2 v-else>{{ $t('employment tenant :number', { number: String(index + 1) }) }}</h2>
    </header>

    <fieldset>
      <div class="group">
        <div class="field">
          <label :for="fieldId('account', index, 'details-work-position')">{{ $t('employment function') }}</label>
          <div>
            <input :id="fieldId('account', index, 'details-work-position')" type="text" v-model="account.details.work.position" />
            <v-form-error :error="errors[`accounts.${index}.details.work.position`]" />
          </div>
        </div>

        <div class="field">
          <label :for="fieldId('account', index, 'details-work-employer')">{{ $t('employer name') }}</label>
          <div>
            <input :id="fieldId('account', index, 'details-work-employer')" type="text" v-model="account.details.work.employer" />
            <v-form-error :error="errors[`accounts.${index}.details.work.employer`]" />
          </div>
        </div>
      </div>

      <div class="group">
        <div class="field">
          <label>{{ $t('employment type') }}</label>
          <div>
            <label class="radio">
              <input type="radio" v-model="account.details.work.type" value="permanent" />
              <span class="label">{{ $t('employment-type:permanent') }}</span>
            </label>
            <label class="radio">
              <input type="radio" v-model="account.details.work.type" value="fixed-term" />
              <span class="label">{{ $t('employment-type:fixed-term') }}</span>
            </label>
            <label class="radio">
              <input type="radio" v-model="account.details.work.type" value="flexible" />
              <span class="label">{{ $t('employment-type:flexible') }}</span>
            </label>
            <label class="radio">
              <input type="radio" v-model="account.details.work.type" value="self-employed" />
              <span class="label">{{ $t('employment-type:self-employed') }}</span>
            </label>
            <v-form-error :error="errors[`accounts.${index}.details.work.type`]" />
          </div>
        </div>

        <div class="field">
          <label :for="fieldId('account', index, 'details-work-hours')">{{ $t('contracted hours per week') }}</label>
          <div>
            <input :id="fieldId('account', index, 'details-work-hours')" type="text" inputmode="numeric" v-model="account.details.work.hours" />
            <v-form-error :error="errors[`accounts.${index}.details.work.hours`]" />
          </div>
        </div>
      </div>
    </fieldset>
  </section>

  <section class="documents">
    <header>
      <h2 v-if="index === 0">{{ $t('upload my documents') }}</h2>
      <h2 v-else>{{ $t('upload documents tenant :number', { number: String(index + 1) }) }}</h2>
    </header>

    <fieldset>
      <div class="field">
        <label :for="fieldId('account', index, 'identification')">{{ $t('proof of identity (passport / ID card / visa)') }}</label>
        <div>
          <v-file-upload :input-id="fieldId('account', index, 'identification')" name="identification" v-model="account.attachments.identification" />
          <p class="help">
            {{
              $t(
                'If you are from outside the EU, you must have a valid visa or residence permit and upload it along with your passport.',
              )
            }}
          </p>
          <v-form-error :error="errors[`accounts.${index}.attachments.identification.id`]" />
        </div>
      </div>

      <div class="field">
        <label
          >{{ $t('visa / residence permit') }} <small>({{ $t('optional') }})</small></label
        >
        <div>
          <v-file-upload name="visa" v-model="account.attachments.visa" />
          <v-form-error :error="errors[`accounts.${index}.attachments.visa.id`]" />
        </div>
      </div>
    </fieldset>

    <fieldset>
      <div class="field">
        <label :for="fieldId('account', index, 'mprextract')">{{ $t('extract from the Municipal Personal Records Database (with address history)') }}</label>
        <div>
          <v-file-upload :input-id="fieldId('account', index, 'mprextract')" name="mprextract" v-model="account.attachments.mprextract" />
          <p class="help">
            {{ $t('You can apply for this online via DigiD or at the counter of the municipality where you live.') }}
          </p>
          <v-form-error :error="errors[`accounts.${index}.attachments.mprextract.id`]" />
        </div>
      </div>
    </fieldset>

    <fieldset>
      <div class="field">
        <label :for="fieldId('account', index, 'incomestatement')">{{ $t('income statement from the Tax Authorities + last 3 payslips') }}</label>
        <div>
          <v-file-upload :input-id="fieldId('account', index, 'incomestatement')" name="incomestatement" v-model="account.attachments.incomestatement" />
          <p class="help">
            {{
              $t(
                'To be able to apply for this property, you need to fill in the income for all persons on your registration and add an income statement from the Tax Authorities. To do this, log in at mijn.belastingdienst.nl with your DigiD details. Alternatively, call 0800-0543 and make sure you have your citizen service number (Burgerservicenummer) handy.',
              )
            }}
            <a href="/static/scheg/docs/voorbeeld-inkomensverklaring.pdf">{{ $t('example') }}</a>
          </p>
          <v-form-error :error="errors[`accounts.${index}.attachments.incomestatement.id`]" />
        </div>
      </div>

      <div class="field">
        <label :for="fieldId('account', index, 'payslip1')">{{ $t('payslip 1 / recent annual income statement') }}</label>
        <div>
          <v-file-upload :input-id="fieldId('account', index, 'payslip1')" name="payslip1" v-model="account.attachments.payslip1" />
          <v-form-error :error="errors[`accounts.${index}.attachments.payslip1.id`]" />
        </div>
      </div>

      <div class="field">
        <label :for="fieldId('account', index, 'payslip2')">{{ $t('payslip :number', { number: '2' }) }}</label>
        <div>
          <v-file-upload :input-id="fieldId('account', index, 'payslip2')" name="payslip2" v-model="account.attachments.payslip2" />
          <v-form-error :error="errors[`accounts.${index}.attachments.payslip2.id`]" />
        </div>
      </div>

      <div class="field">
        <label :for="fieldId('account', index, 'payslip3')">{{ $t('payslip :number', { number: '3' }) }}</label>
        <div>
          <v-file-upload :input-id="fieldId('account', index, 'payslip3')" name="payslip3" v-model="account.attachments.payslip3" />
          <v-form-error :error="errors[`accounts.${index}.attachments.payslip3.id`]" />
        </div>
      </div>
    </fieldset>

    <fieldset>
      <div class="field">
        <label :for="fieldId('account', index, 'landlordstatement')">{{ $t('landlord statement') }}</label>
        <div>
          <v-file-upload :input-id="fieldId('account', index, 'landlordstatement')" name="landlordstatement" v-model="account.attachments.landlordstatement" />
          <p class="help">
            {{
              $t(
                'A landlord statement is a document provided by your current or previous landlord that contains information about your rental behavior, such as paying rent and whether you’ve caused any nuisance.',
              )
            }}
            <a href="/static/scheg/docs/verhuurdersverklaring.pdf">{{ $t('example') }}</a>
          </p>
          <v-form-error :error="errors[`accounts.${index}.attachments.landlordstatement.id`]" />
        </div>
      </div>
    </fieldset>

    <fieldset v-if="isStudent">
      <div class="field">
        <label :for="fieldId('account', index, 'studyregistration')">{{ $t('proof of enrollment from an educational institution (WO/HBO/MBO)') }}</label>
        <div>
          <v-file-upload :input-id="fieldId('account', index, 'studyregistration')" name="studyregistration" v-model="account.attachments.studyregistration" />
          <v-form-error :error="errors[`accounts.${index}.attachments.studyregistration.id`]" />
        </div>
      </div>
    </fieldset>
  </section>
</template>

<script lang="ts" setup>
import { useRegistrationContext } from '/@front:shared/composables/useRegistrationContext'
import type { Account } from '/@front:shared/registration'
import { fieldId } from '/@front:shared/utils'

import AddressFields from './AddressFields.vue'
import BackupContactFields from './BackupContactFields.vue'
import FinancialIncomeFields from './FinancialIncomeFields.vue'
import PersonalDetailsFields from './PersonalDetailsFields.vue'
import TenantContactFields from './TenantContactFields.vue'
import TenantRelationFields from './TenantRelationFields.vue'

defineProps<{
  account: Account
  index: number
  errors: Record<string, string>
  isStudent: boolean
}>()

const emit = defineEmits<{
  (e: 'validate-email', index: number): void
  (e: 'apply-did-you-mean', index: number): void
}>()

const { countries, studyInstitutions } = useRegistrationContext()
</script>
